import qs from 'qs';
import api from '@/services/ApiService';
import proxy from '@/services/ProxyService';
import { useInterfaceStore } from '@/store/interface';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import tenant from '@/services/TenantService';
import { URL_PREFIX } from './FormField';

export const SIMPLE_STATE_KEYS = ['partyType'];
export const ARRAY_STATE_KEYS = [
  'departments',
  'assignedOrganizations',
  'domains',
];

export const POSSIBLE_SOURCES = {
  weclapp: { name: 'Weclapp', icon: '/static/weclappIcon.png' },
  tanss: { name: 'Tanss', icon: '/static/tanssIcon.png' },
};

export const EDIT_SOURCE = { name: 'Edit', icon: <CreateOutlinedIcon /> };
export const CODEMETA_SOURCE = { name: 'Codemeta', icon: '/static/logo.svg' };

export const SALUTATIONS = [
  { value: 'MR', label: 'Herr' },
  { value: 'MRS', label: 'Frau' },
  { value: 'COMPANY', label: 'Firma' },
  { value: 'FAMILY', label: 'Familie' },
  { value: 'NO_SALUTATION', label: 'Keine' },
];

export const SALUTATIONS_REVERSE = {
  1: 'MR',
  2: 'MRS',
};

export const LEAD_STATUS = [
  { value: 'NONE', label: 'Kein Interessent' },
  { value: 'NEW', label: 'Neu' },
  { value: 'PREQUALIFIED', label: 'Vorqualifiziert' },
  { value: 'QUALIFIED', label: 'Qualifiziert' },
  { value: 'DISQUALIFIED', label: 'Disqualifiziert' },
  { value: 'CONVERTED', label: 'Konvertiert' },
];

const isValidUrl = (url) => {
  if (!url) return false;
  try {
    const normalized = String(url).startsWith('http')
      ? url
      : `${URL_PREFIX}${url}`;
    const parsed = new URL(normalized);
    return !!parsed.hostname && !/\s/.test(parsed.hostname);
  } catch {
    return false;
  }
};

const transformAndValidateUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string' || rawUrl.trim() === '') {
    return null;
  }
  let url = rawUrl.trim();
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `${URL_PREFIX}${url}`;
  }
  if (url.startsWith('http://')) {
    url = url.replace('http://', 'https://');
  }
  if (isValidUrl(url)) {
    return url;
  }
  return null;
};

export const getRecordIdBySourceId = async (source, sourceId, sourceType) => {
  const { data } = await api.get(
    `/datasource?source=${source}&sourceId=${sourceId}${sourceType ? `&sourceType=${sourceType}` : ''}`,
  );
  return data?.result[0]?.recordId || null;
};

export const getRecordIdsBySourceIds = async (
  source,
  sourceIds,
  sourceType,
) => {
  if (!sourceIds || sourceIds.length === 0) {
    return {};
  }

  const results = await Promise.all(
    sourceIds.map(async (sourceId) => {
      const recordId = await getRecordIdBySourceId(
        source,
        sourceId,
        sourceType,
      );
      return { sourceId, recordId };
    }),
  );

  const idMap = results.reduce((acc, { sourceId, recordId }) => {
    if (recordId) {
      acc[sourceId] = recordId;
    }
    return acc;
  }, {});
  return idMap;
};

async function processTanssCompany(fullCompany) {
  if (!fullCompany) return { data: null };
  const INTERFACE_STORE = useInterfaceStore.getState();

  try {
    const parentPromise =
      fullCompany.headquarterId && fullCompany.headquarterId !== 0
        ? getRecordIdBySourceId('tanss', fullCompany.headquarterId, 'company')
        : Promise.resolve(null);

    const employeesPromise = proxy.get(
      `/${INTERFACE_STORE.tanss}/companies/${fullCompany.id}/employees`,
    );

    const [parentPartyId, employeesData] = await Promise.all([
      parentPromise,
      employeesPromise,
    ]);

    if (fullCompany.headquarterId && !parentPartyId) {
      return {
        error: `Aus Tanss wurde die Hauptfirma (ID ${fullCompany.headquarterId}) noch nicht übertragen.`,
      };
    }

    const employees = employeesData?.data?.content || [];
    const employeeIds = employees.map((e) => e.id);
    let contacts = [];
    const warnings = [];

    if (employeeIds.length > 0) {
      const recordIdMap = await getRecordIdsBySourceIds(
        'tanss',
        employeeIds,
        'employee',
      );
      await employees.map(async (employee) => {
        const recordId = recordIdMap[employee.id];
        if (recordId) {
          const {
            data: { content },
          } = await proxy.get(
            `/${INTERFACE_STORE.tanss}/employees/${employee.id}`,
          );
          contacts.push({
            id: recordId,
            contact:
              content.companyAssignments?.find(
                (cA) => cA.companyId === fullCompany.id,
              )?.contactPerson || false,
          });
        } else {
          warnings.push(
            `Die Person ${employee.name} (ID ${employee.id}) wurde in Tanss gefunden.`,
          );
        }
      });
    }

    for (const category in INTERFACE_STORE.config.tanssCategories) {
      const { field, map } = INTERFACE_STORE.config.tanssCategories[category];
      const type = fullCompany.types?.find(
        (t) => t.categoryId === parseInt(category, 10),
      );
      if (type) {
        const keyForValue = Object.keys(map).find((k) => map[k] === type.id);
        if (keyForValue) fullCompany[field] = keyForValue;
      }
    }

    return {
      data: {
        ...fullCompany,
        partyType: fullCompany.personalCustomer ? 'PERSON' : 'ORGANIZATION',
        contacts,
        parentPartyId,
      },
      warnings,
    };
  } catch (err) {
    console.error('Fehler bei der Verarbeitung der Tanss-Firma:', err);
    return { error: 'Ein unerwarteter Fehler ist aufgetreten.' };
  }
}

async function processTanssEmployee(fullEmployee) {
  if (!fullEmployee) return { data: null };

  try {
    const assignments = fullEmployee?.companyAssignments || [];
    if (assignments.length === 0) {
      return {
        data: { ...fullEmployee, partyType: 'PERSON', companyAssignments: [] },
      };
    }

    const companyIds = assignments.map((a) => a.companyId);
    const recordIdMap = await getRecordIdsBySourceIds(
      'tanss',
      companyIds,
      'company',
    );

    const { companyAssignments, errors } = assignments.reduce(
      (acc, assignment) => {
        const recordId = recordIdMap[assignment.companyId];
        if (recordId) {
          acc.companyAssignments.push({
            id: recordId,
            primary: false,
            contact: assignment?.contactPerson || false,
          });
        } else {
          acc.errors.push(
            `Die dem Kontakt zugewiesene Firma (ID ${assignment.companyId}) wurde noch nicht übertragen.`,
          );
        }
        return acc;
      },
      { companyAssignments: [], errors: [] },
    );

    if (errors.length > 0) {
      return { error: `Fehler bei der Suche in Tanss: ${errors.join(' ')}` };
    }

    return {
      data: { ...fullEmployee, partyType: 'PERSON', companyAssignments },
    };
  } catch (err) {
    console.error('Fehler bei der Verarbeitung des Tanss-Mitarbeiters:', err);
    return { error: 'Ein unerwarteter Fehler ist aufgetreten.' };
  }
}

async function processWeclappParty(foundParty) {
  if (!foundParty) return { data: null };

  try {
    const contactIds = foundParty.contacts?.map((c) => c.id) || [];
    const orgIds = foundParty.assignedOrganizations?.map((org) => org.id) || [];

    const [parentPartyId, contactsIdMap, orgsIdMap, usersResponse] =
      await Promise.all([
        foundParty.parentPartyId
          ? getRecordIdBySourceId('weclapp', foundParty.parentPartyId)
          : Promise.resolve(null),
        contactIds.length > 0
          ? getRecordIdsBySourceIds('weclapp', contactIds)
          : Promise.resolve({}),
        orgIds.length > 0
          ? getRecordIdsBySourceIds('weclapp', orgIds)
          : Promise.resolve({}),
        foundParty.responsibleUserId
          ? tenant.get('/user')
          : Promise.resolve(null),
      ]);

    if (foundParty.parentPartyId && !parentPartyId) {
      return {
        error: `Aus Weclapp wurde die Hauptfirma mit der ID "${foundParty.parentPartyId}" noch nicht übertragen.`,
      };
    }

    const { contacts, warnings } = (foundParty.contacts || []).reduce(
      (acc, c) => {
        const recordId = contactsIdMap[c.id];
        if (recordId) {
          acc.contacts.push({
            id: recordId,
            primary: foundParty.primaryContactId === c.id,
            contact: true,
          });
        } else {
          acc.warnings.push(
            `Die der Firma zugewiesene Kontakt (ID ${c.id}) wurde noch nicht übertragen.`,
          );
        }
        return acc;
      },
      { contacts: [], warnings: [] },
    );

    const { companyAssignments, errors } = (
      foundParty.assignedOrganizations || []
    ).reduce(
      (acc, org) => {
        const recordId = orgsIdMap[org.id];
        if (recordId) {
          acc.companyAssignments.push({
            id: recordId,
            primary: false,
            contact: true,
          });
        } else {
          acc.errors.push(
            `Die dem Kontakt zugewiesene Firma (ID ${org.id}) wurde noch nicht übertragen.`,
          );
        }
        return acc;
      },
      { companyAssignments: [], errors: [] },
    );

    if (errors.length > 0) {
      return { error: `Fehler bei der Suche in Weclapp: ${errors.join(' ')}` };
    }

    let responsibleUser = null;
    let responsibleUserWarnings = [];
    if (foundParty.responsibleUserId && usersResponse?.data) {
      responsibleUser =
        usersResponse.data.find(
          (u) => u.weclappId === foundParty.responsibleUserId,
        ) || null;
      if (!responsibleUser) {
        responsibleUserWarnings.push(
          `Der verantwortliche Benutzer ${foundParty.responsibleUserUsername} (${foundParty.responsibleUserId}) wurde nicht gefunden.`,
        );
      }
    }

    return {
      data: {
        ...foundParty,
        contacts,
        companyAssignments,
        parentPartyId,
        responsibleUserId: responsibleUser?._id || null,
      },
      warnings,
      responsibleUserWarnings,
    };
  } catch (err) {
    console.error('Fehler bei der Verarbeitung der Weclapp-Party:', err);
    return { error: 'Ein unerwarteter Fehler ist aufgetreten.' };
  }
}

async function processCodemetaOrganization(foundParty) {
  if (!foundParty) return { data: null };

  try {
    const parentPromise = foundParty.parentPartyId
      ? api.get(`/party/${foundParty.parentPartyId}`)
      : Promise.resolve();

    const contactsPromise = foundParty.contacts?.length
      ? api.get(
          `/party?${qs.stringify({ _id: { $in: foundParty.contacts.map((c) => c.id) } })}`,
        )
      : Promise.resolve();

    const [parentResponse, contactsResponse] = await Promise.all([
      parentPromise,
      contactsPromise,
    ]);

    if (foundParty.parentPartyId && !parentResponse?.data) {
      return {
        error: `Die Hauptfirma (ID ${foundParty.parentPartyId}) wurde in Codemeta nicht gefunden.`,
      };
    }

    const warnings = [];
    if (contactsResponse?.data?.result) {
      const foundContactIds = new Set(
        contactsResponse.data.result.map((c) => c._id),
      );
      foundParty.contacts.forEach((cont) => {
        if (!foundContactIds.has(cont.id)) {
          warnings.push(
            `Der der Firma zugewiesene Kontakt (ID ${cont.id}) wurde in Codemeta nicht gefunden.`,
          );
        }
      });
    }

    return { data: { ...foundParty }, warnings };
  } catch (err) {
    console.error('Fehler bei der Verarbeitung der Codemeta-Party:', err);
    return { error: 'Ein unerwarteter Fehler ist aufgetreten.' };
  }
}

export const searchInTanssCompanyById = async (id) => {
  console.log(`Rufe Tanss-Firma via ID ab: ${id}`);
  const INTERFACE_STORE = useInterfaceStore.getState();
  const { data: fullCompanyRes } = await proxy.get(
    `/${INTERFACE_STORE.tanss}/companies/${id}`,
  );
  return processTanssCompany(fullCompanyRes?.content);
};

export const searchInTanssEmployeeById = async (id) => {
  console.log(`Rufe Tanss-Mitarbeiter via ID ab: ${id}`);
  const INTERFACE_STORE = useInterfaceStore.getState();
  const { data: fullEmployeeRes } = await proxy.get(
    `/${INTERFACE_STORE.tanss}/employees/${id}`,
  );
  return processTanssEmployee(fullEmployeeRes?.content);
};

export const searchInTanssForEmail = async (searchTerm) => {
  console.log(`Suche-Tanss-Email: ${searchTerm}`);
  const INTERFACE_STORE = useInterfaceStore.getState();
  const { data } = await proxy.put(`${INTERFACE_STORE.tanss}/search`, {
    areas: ['EMPLOYEE'],
    query: searchTerm.toLowerCase(),
  });
  const foundEmployees = data?.content?.employees;

  if (!Array.isArray(foundEmployees))
    return {
      error: 'Unerwartetes Antwortformat bei der Suche nach Personen in Tanss',
    };
  if (foundEmployees.length > 1)
    return { error: `Mehrere Personen gefunden. Suche muss eindeutig sein!` };
  if (!foundEmployees[0]) return { data: null };

  const { data: fullEmployeeRes } = await proxy.get(
    `/${INTERFACE_STORE.tanss}/employees/${foundEmployees[0].id}`,
  );
  return processTanssEmployee(fullEmployeeRes?.content);
};

export const searchInTanssForCustomerNumber = async (searchTerm) => {
  console.log(`Suche-Tanss-CustomerNumber: ${searchTerm}`);
  const INTERFACE_STORE = useInterfaceStore.getState();
  const term = searchTerm.toLowerCase();
  const { data } = await proxy.put(`${INTERFACE_STORE.tanss}/search`, {
    areas: ['COMPANY'],
    query: term,
  });
  const companies = data?.content?.companies;

  if (!Array.isArray(companies))
    return {
      error: 'Unerwartetes Antwortformat bei der Suche nach Firmen in Tanss',
    };

  const foundCompanies = companies.filter(
    (c) =>
      c.displayId.toLowerCase() === term &&
      c.types?.some((t) => t.name === 'Kunde'),
  );
  if (foundCompanies.length > 1)
    return { error: `Es existieren mehrere Firmen mit dieser Kundennummer.` };
  if (!foundCompanies[0]) return { data: null };

  const { data: fullCompanyRes } = await proxy.get(
    `/${INTERFACE_STORE.tanss}/companies/${foundCompanies[0].id}`,
  );
  return processTanssCompany(fullCompanyRes?.content);
};

export const searchInTanssForSupplierNumber = async (searchTerm) => {
  console.log(`Suche-Tanss-SupplierNumber: ${searchTerm}`);
  const INTERFACE_STORE = useInterfaceStore.getState();
  const term = searchTerm.toLowerCase();
  const { data } = await proxy.put(`${INTERFACE_STORE.tanss}/search`, {
    areas: ['COMPANY'],
    query: term,
  });
  const companies = data?.content?.companies;

  if (!Array.isArray(companies))
    return {
      error: 'Unerwartetes Antwortformat bei der Suche nach Firmen in Tanss',
    };

  const foundCompanies = companies.filter(
    (c) =>
      c.displayId.toLowerCase() === term &&
      c.types?.some((t) => t.name === 'Lieferant'),
  );
  if (foundCompanies.length > 1)
    return { error: `Mehrere Firmen gefunden. Suche muss eindeutig sein!` };
  if (!foundCompanies[0]) return { data: null };

  const { data: fullCompanyRes } = await proxy.get(
    `/${INTERFACE_STORE.tanss}/companies/${foundCompanies[0].id}`,
  );
  return processTanssCompany(fullCompanyRes?.content);
};

export const searchInWeclappPartyById = async (id) => {
  const INTERFACE_STORE = useInterfaceStore.getState();
  const { data: foundParty } = await proxy.get(
    `${INTERFACE_STORE.weclapp}/party/id/${id}`,
  );

  if (foundParty && foundParty.id) {
    const { data: orgsData } = await proxy.get(
      `${INTERFACE_STORE.weclapp}/party?partyType-eq=ORGANIZATION&contacts.id-eq=${foundParty.id}`,
    );
    foundParty.assignedOrganizations = orgsData?.result || [];
  }

  return processWeclappParty(foundParty);
};

export const searchInWeclappForEmail = async (searchTerm) => {
  console.log(`Suche-Weclapp-Email: ${searchTerm}`);
  const INTERFACE_STORE = useInterfaceStore.getState();
  const { data } = await proxy.get(
    `${INTERFACE_STORE.weclapp}/party?partyType-eq=PERSON&email-eq=${searchTerm.toLowerCase()}`,
  );
  const parties = data?.result || [];

  if (!Array.isArray(parties))
    return {
      error:
        'Unerwartetes Antwortformat bei der Suche nach Personen in Weclapp',
    };
  if (parties.length > 1)
    return { error: `Mehrere Personen gefunden. Suche muss eindeutig sein!` };
  if (!parties[0]) return { data: null };

  const foundParty = parties[0];
  const { data: orgsData } = await proxy.get(
    `${INTERFACE_STORE.weclapp}/party?contacts.id-eq=${foundParty.id}`,
  );
  foundParty.assignedOrganizations = orgsData?.result || [];

  return processWeclappParty(foundParty);
};

export const searchInWeclappForCustomerNumber = async (searchTerm) => {
  console.log(`Suche-Weclapp-CustomerNumber: ${searchTerm}`);
  const INTERFACE_STORE = useInterfaceStore.getState();
  const { data } = await proxy.get(
    `${INTERFACE_STORE.weclapp}/party?partyType-eq=ORGANIZATION&customerNumber-eq=${searchTerm}`,
  );
  const parties = data?.result || [];

  if (!Array.isArray(parties))
    return {
      error: 'Unerwartetes Antwortformat bei der Suche nach Kunden in Weclapp',
    };
  if (parties.length > 1)
    return {
      error: `Es wurden mehrere Firmen mit dieser Kundennummer gefunden.`,
    };

  return processWeclappParty(parties[0]);
};

export const searchInWeclappForSupplierNumber = async (searchTerm) => {
  console.log(`Suche-Weclapp-SupplierNumber: ${searchTerm}`);
  const INTERFACE_STORE = useInterfaceStore.getState();
  const { data } = await proxy.get(
    `${INTERFACE_STORE.weclapp}/party?partyType-eq=ORGANIZATION&supplierNumber-eq=${searchTerm}`,
  );
  const parties = data?.result || [];

  if (!Array.isArray(parties))
    return {
      error:
        'Unerwartetes Antwortformat bei der Suche nach Lieferanten in Weclapp',
    };
  if (parties.length > 1)
    return {
      error: `Mehrere Lieferanten gefunden. Suche muss eindeutig sein!`,
    };

  return processWeclappParty(parties[0]);
};

export const searchInCodemetaForEmail = async (searchTerm) => {
  console.log(`Suche-Codemeta-Email: ${searchTerm}`);
  const { data } = await api.get(
    `/party?partyType=PERSON&email=${searchTerm.toLowerCase()}`,
  );
  const parties = data?.result || [];

  if (!Array.isArray(parties))
    return {
      error:
        'Unerwartetes Antwortformat bei der Suche nach Personen in Codemeta',
    };
  if (parties.length > 1)
    return { error: `Mehrere Personen gefunden. Suche muss eindeutig sein!` };
  if (!parties[0]) return { data: null };

  const foundParty = parties[0];
  const { data: orgsData } = await api.get(
    `/party?contacts.id=${foundParty._id}`,
  );
  const assignedOrganizations = (orgsData?.result || []).map((a) => {
    const contact = a.contacts?.find((c) => c.id === foundParty._id) || {};
    return { id: a._id, contact: contact.contact };
  });

  return { data: { ...foundParty, assignedOrganizations } };
};

export const searchInCodemetaForCustomerNumber = async (searchTerm) => {
  console.log(`Suche-Codemeta-CustomerNumber: ${searchTerm}`);
  const { data } = await api.get(
    `/party?partyType=ORGANIZATION&customerNumber=${searchTerm.toLowerCase()}`,
  );
  const parties = data?.result || [];

  if (!Array.isArray(parties))
    return {
      error: 'Unerwartetes Antwortformat bei der Suche nach Firmen in Codemeta',
    };
  if (parties.length > 1)
    return {
      error: `Es wurden mehrere Firmen mit dieser Kundennummer gefunden.`,
    };

  return processCodemetaOrganization(parties[0]);
};

export const searchInCodemetaForSupplierNumber = async (searchTerm) => {
  console.log(`Suche-Codemeta-SupplierNumber: ${searchTerm}`);
  const { data } = await api.get(
    `/party?partyType=ORGANIZATION&supplierNumber=${searchTerm.toLowerCase()}`,
  );
  const parties = data?.result || [];

  if (!Array.isArray(parties))
    return {
      error: 'Unerwartetes Antwortformat bei der Suche nach Firmen in Codemeta',
    };
  if (parties.length > 1)
    return {
      error: `Es wurden mehrere Firmen mit der Lieferantennummer gefunden.`,
    };
  return processCodemetaOrganization(parties[0]);
};

export const transformToInternalSchema = (source, partyData) => {
  if (!partyData) return {};
  switch (source) {
    case 'weclapp': {
      let {
        id = null,
        firstName = '',
        lastName = '',
        email = null,
        salutation = 'NO_SALUTATION',
        birthDate = null,
        company = null,
        company2 = null,
        partyType = 'ORGANIZATION',
        customerNumber = '',
        supplierNumber = '',
        isCustomer = false,
        isSupplier = false,
        isLead = false,
        leadStatus = 'NONE',
        phone = null,
        fax = null,
        website = null,
        mobilePhone1 = null,
        mobilePhone2 = null,
        phoneHome = null,
        departments = [],
        domains = [],
        parentPartyId = null,
        responsibleUserId = null,
        contacts = [],
        companyAssignments = [],
        addresses = [],
        referenceNumber = '',
      } = partyData;

      const transformedWebsite = transformAndValidateUrl(website);

      addresses = addresses.map((a) => ({
        city: a.city || '',
        zipcode: a.zipcode || '',
        street1: a.street1 || '',
        street2: a.street2 || '',
        countryCode: a.countryCode || '',
        primeAddress: Boolean(a.primeAddress),
        id: a.id || null,
      }));

      const firstPrimeIdx = addresses.findIndex((a) => a.primeAddress);
      if (firstPrimeIdx === -1 && addresses[0]) {
        addresses[0].primeAddress = true;
      } else if (firstPrimeIdx !== -1) {
        addresses.forEach((a, i) => {
          a.primeAddress = i === firstPrimeIdx;
        });
      }

      return {
        id,
        firstName,
        lastName,
        email,
        salutation,
        birthDate: birthDate ? new Date(birthDate) : null,
        company,
        company2,
        partyType,
        customerNumber,
        isCustomer: !!customerNumber || isCustomer,
        supplierNumber,
        isSupplier: !!supplierNumber || isSupplier,
        leadStatus,
        isLead,
        phone,
        fax,
        website: transformedWebsite,
        mobilePhone1,
        mobilePhone2,
        phoneHome,
        departments,
        domains,
        parentPartyId,
        contacts,
        assignedOrganizations: companyAssignments.map(
          ({ recordId, id, contact }) => ({
            id: recordId ?? id,
            contact,
          }),
        ),
        addresses,
        responsibleUserId,
        referenceNumber,
      };
    }
    case 'tanss': {
      if (partyData.partyType === 'PERSON') {
        return {
          id: partyData.id || null,
          initials: partyData.initials || '',
          partyType: partyData.partyType,
          firstName: partyData?.firstName || '',
          lastName: partyData?.lastName || '',
          email: partyData.emailAddress || null,
          salutation:
            SALUTATIONS_REVERSE[partyData.salutationId] || 'NO_SALUTATION',
          birthDate:
            partyData.birthday && partyData.birthday !== '0000-00-00'
              ? new Date(partyData.birthday)
              : null,
          phone: partyData.telephoneNumber || null,
          fax: partyData.personalFaxNumber || null,
          mobilePhone1: partyData.mobilePhone || null,
          mobilePhone2: partyData.mobileNumberTwo || null,
          phoneHome: partyData.privatePhoneNumber || null,
          departments: partyData.departments || [],
          domains: partyData.domains || [],
          parentPartyId: partyData.parentPartyId || null,
          contacts: partyData.contacts || [],
          assignedOrganizations:
            partyData.companyAssignments?.map(({ recordId, id, contact }) => ({
              id: recordId ?? id,
              contact,
            })) || [],
          addresses: [],
          referenceNumber: partyData.matchcode || '',
        };
      } else {
        // console.log('Transforming Tanss Organization:', partyData);

        const isCustomer = partyData?.types?.some((t) => t.id === 1) || false;
        const isSupplier = partyData?.types?.some((t) => t.id === 2) || false;
        const isLead = partyData?.types?.some((t) => t.id === 3) || false;

        const nameLines = partyData?.name.split('\n');
        const [company, company2] = partyData?.personalCustomer
          ? [undefined, undefined]
          : [nameLines[0], nameLines[1] || undefined];

        let firstName, lastName;
        if (partyData?.personalCustomer) {
          const nameParts = partyData?.name.split(', ');
          if (nameParts.length > 1) {
            [lastName, firstName] = nameParts;
          } else {
            const parts = partyData?.name.split(' ');
            firstName = parts.slice(0, -1).join(' ') || undefined;
            lastName = parts.slice(-1)[0] || undefined;
          }
        }

        const transformedWebsite = transformAndValidateUrl(partyData?.website);

        return {
          id: partyData.id || null,
          company,
          company2,
          partyType: partyData?.personalCustomer ? 'PERSON' : 'ORGANIZATION',
          isCustomer: isCustomer || isLead,
          customerNumber: isCustomer ? partyData?.displayId : undefined,
          isSupplier: isSupplier,
          supplierNumber: isSupplier ? partyData?.displayId : undefined,
          leadStatus: isLead ? partyData?.leadStatus : undefined,
          phone: partyData?.telephone,
          fax: partyData?.telefax,
          addresses: [
            {
              street1: partyData?.street,
              zipcode: partyData?.postcode,
              city: partyData?.city,
              countryCode: partyData?.country || undefined,
              primeAddress: true,
            },
          ],
          website: transformedWebsite,
          email: partyData?.email || null,
          initials: partyData?.initials,
          firstName,
          lastName,
          mobilePhone1: partyData?.mobilePhone,
          mobilePhone2: partyData?.mobileNumberTwo,
          responsibleUserId: partyData?.responsibleUserId || null,
          parentPartyId:
            partyData.parentPartyId ||
            (partyData?.headquarterId !== 0 &&
              String(partyData.headquarterId)) ||
            null,
          referenceNumber: partyData?.matchcode || '',
        };
      }
    }
    case 'codemeta':
    default: {
      const transformedWebsite = transformAndValidateUrl(partyData?.website);
      return {
        ...partyData,
        firstName: partyData.firstName || '',
        lastName: partyData.lastName || '',
        email: partyData.email || null,
        salutation: partyData.salutation || 'NO_SALUTATION',
        birthDate: partyData.birthDate || null,
        company: partyData.company || null,
        company2: partyData.company2 || null,
        partyType: partyData.partyType || 'ORGANIZATION',
        customerNumber: partyData.customerNumber || '',
        isCustomer: !!partyData.customerNumber || partyData.isCustomer || false,
        supplierNumber: partyData.supplierNumber || '',
        isSupplier: !!partyData.supplierNumber || partyData.isSupplier || false,
        leadStatus: partyData.leadStatus || 'NONE',
        isLead: partyData.isLead || false,
        phone: partyData.phone || null,
        fax: partyData.fax || null,
        website: transformedWebsite,
        mobilePhone1: partyData.mobilePhone1 || null,
        mobilePhone2: partyData.mobilePhone2 || null,
        phoneHome: partyData.phoneHome || null,
        departments: partyData.departments || [],
        domains: partyData.domains || [],
        parentPartyId: partyData.parentPartyId || null,
        contacts: partyData.contacts || [],
        assignedOrganizations: partyData.assignedOrganizations || [],
        addresses:
          partyData.addresses?.length > 0
            ? partyData.addresses
            : [
                {
                  street1: '',
                  street2: '',
                  zipcode: '',
                  city: '',
                  countryCode: 'DE',
                  primeAddress: true,
                },
              ],
        id: partyData._id || null,
        responsibleUserId: partyData.responsibleUserId || null,
        referenceNumber: partyData.referenceNumber || '',
      };
    }
  }
};

export const mergeParties = (base, addition) => {
  if (!base) return addition || null;
  if (!addition) return base || null;

  const merged = { ...base };

  // Filter out keys that need a more complex merge
  const { contacts, addresses, ...rest } = addition;
  Object.assign(merged, rest);

  // Merge contacts
  const contactMap = {};
  (base.contacts || []).forEach((c) => {
    if (c && c.id) contactMap[c.id] = { ...c };
  });
  (addition.contacts || []).forEach((c) => {
    if (c && c.id) {
      if (contactMap[c.id]) {
        contactMap[c.id] = { ...contactMap[c.id], ...c };
      } else {
        contactMap[c.id] = { ...c };
      }
    }
  });
  merged.contacts = Object.values(contactMap);

  // Merge addresses
  const addressMap = {};
  (base.addresses || []).forEach((a) => {
    const key = `${a.street1}|${a.zipcode}|${a.city}|${a.countryCode}`;
    addressMap[key] = { ...a };
  });
  (addition.addresses || []).forEach((a) => {
    const key = `${a.street1}|${a.zipcode}|${a.city}|${a.countryCode}`;
    if (addressMap[key]) {
      addressMap[key] = { ...addressMap[key], ...a };
    } else {
      addressMap[key] = { ...a };
    }
  });
  merged.addresses = Object.values(addressMap);

  // Ensure only one primary address
  const firstPrimeIdx = merged.addresses.findIndex((a) => a.primeAddress);
  if (firstPrimeIdx === -1 && merged.addresses[0]) {
    merged.addresses[0].primeAddress = true;
  } else if (firstPrimeIdx !== -1) {
    merged.addresses.forEach((a, i) => {
      a.primeAddress = i === firstPrimeIdx;
    });
  }

  return merged;
};
